import React, { useMemo, useState } from "react";
import { MessageCircle, Copy, ExternalLink, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useSettings } from "../lib/useSettings";

const DEFAULT_TEMPLATE = "🔴 *{title}*\n\n{summary}\n\nपूरी खबर पढें: {url}\n\n{hashtags}\n— Bodh Networks · Not Just News. Awareness.\nFollow @bodh_networks";

function buildMessage(template, article, defaultHashtags) {
  const url = `${window.location.origin}/article/${article.slug}`;
  const summary = article.summary || article.subtitle || "";
  const tagsLine = (article.tags || []).map((t) => `#${String(t).replace(/\s+/g, "")}`).join(" ");
  const hashtags = (tagsLine ? tagsLine + " " : "") + (defaultHashtags || "");
  return (template || DEFAULT_TEMPLATE)
    .replaceAll("{title}", article.title || "")
    .replaceAll("{summary}", summary)
    .replaceAll("{url}", url)
    .replaceAll("{category}", article.category || "")
    .replaceAll("{hashtags}", hashtags.trim())
    .replaceAll("{author}", article.author || "Bodh Desk");
}

export default function WhatsAppBroadcastModal({ article, open, onClose }) {
  const { settings } = useSettings();
  const broadcast = settings?.broadcast || {};
  const initialMsg = useMemo(
    () => buildMessage(broadcast.whatsapp_template, article || {}, broadcast.default_hashtags),
    [article, broadcast.whatsapp_template, broadcast.default_hashtags]
  );
  const [msg, setMsg] = useState(initialMsg);
  const [copied, setCopied] = useState(false);

  React.useEffect(() => { setMsg(initialMsg); }, [initialMsg]);

  if (!open || !article) return null;

  const wahUrl = `https://wa.me/?text=${encodeURIComponent(msg)}`;
  const charCount = msg.length;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(msg);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("मैसेज कॉपी हुआ — WhatsApp में पेस्ट करें");
    } catch {
      toast.error("कॉपी नहीं हो सका");
    }
  };

  const logBroadcast = async () => {
    try { await api.post(`/newsroom/articles/${article.id}/broadcast-log?channel=whatsapp`); } catch {}
  };

  const openWA = () => {
    logBroadcast();
    window.open(wahUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="wa-broadcast-modal" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-emerald-600 to-green-500 p-5 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-white/20 flex items-center justify-center">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-outfit font-black text-lg">WhatsApp Broadcast</h3>
              <p className="text-xs text-emerald-50 font-hind">अपनी Broadcast List पर भेजें</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="wa-close" className="h-9 w-9 rounded-full hover:bg-white/20 flex items-center justify-center"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5">
          <p className="font-devanagari text-sm font-bold text-zinc-800 line-clamp-2">{article.title}</p>

          <label className="mt-4 block text-xs font-outfit font-bold uppercase tracking-widest text-zinc-500">मैसेज प्रिव्यू (एडिट कर सकते हैं)</label>
          <textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={9}
            data-testid="wa-message"
            className="mt-2 w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-green-600 outline-none font-devanagari text-sm resize-y bg-zinc-50"
          />
          <div className="mt-1 flex items-center justify-between text-[11px] text-zinc-500 font-hind">
            <span>{charCount} कैरेक्टर</span>
            <span>{charCount > 4096 ? "⚠️ WhatsApp की 4096 कैरेक्टर सीमा से अधिक" : "✓ सीमा में"}</span>
          </div>

          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <button
              onClick={copy}
              data-testid="wa-copy"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-zinc-200 text-zinc-800 font-outfit font-bold text-sm hover:bg-zinc-50 transition"
            >
              {copied ? <><Check className="h-4 w-4 text-green-600" /> कॉपी हुआ</> : <><Copy className="h-4 w-4" /> मैसेज कॉपी करें</>}
            </button>
            <button
              onClick={openWA}
              data-testid="wa-open"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-green-500 text-white font-outfit font-bold text-sm hover:opacity-95 transition shadow-lg shadow-emerald-200"
            >
              <ExternalLink className="h-4 w-4" /> WhatsApp में खोलें
            </button>
          </div>

          <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-100 text-xs font-hind text-amber-900">
            <b>कैसे भेजें:</b> "WhatsApp में खोलें" क्लिक करें → अपनी Broadcast List चुनें → भेजें.
            (या) "मैसेज कॉपी करें" क्लिक करें और WhatsApp Web में Broadcast List पर पेस्ट करें.
          </div>
        </div>
      </div>
    </div>
  );
}
